<script setup>
import { ref } from 'vue'
import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuRoot,
  NavigationMenuTrigger,
  NavigationMenuViewport,
} from 'reka-ui'

import Link from '@/Components/Links/Link.vue'
import SiteNavigationListItem from '@/Layouts/Navigation//SiteNavigationListItem.vue'
import ChevronDownIcon from '@svgs/site-nav-chevron-down.svg'

defineProps({
  items: Array,
})

// Checks if there are any nav links within the menu
const hasMoreThanOneGrandChild = (item) => {
  return item.children.some((child) => child.children.length > 1)
}

const currentTrigger = ref('')
</script>

<template>
  <NavigationMenuRoot
    v-model="currentTrigger"
    class="hidden lg:flex z-20 w-full ml-8"
  >
    <NavigationMenuList class="m-0 flex list-none gap-10 items-center rounded-lg p-1 w-full">
      <NavigationMenuItem v-for="item in items">
        <NavigationMenuTrigger
          v-if="hasMoreThanOneGrandChild(item)"
          class="font-sans-pt text-white text-[1.125rem] font-bold leading-6 hover:bg-indigo-dark group flex select-none items-center justify-between gap-2 p-3 outline-none rounded-md focus:ring-2 focus:ring-white"
          :class="{ 'rounded-b-none border-b-[3px] border-coral': item.is_current || item.is_parent }"
          aria-label="{`Open {{ item.title }} menu`}"
        >
          <Link
            class="text-left text-white no-underline text-balance"
            :href="item.url"
            aria-label="{`{{ item.title }} page`}"
          >
            {{ item.title }}
          </Link>
          <ChevronDownIcon
            class="min-w-[28px]"
            role="presentation"
          />
        </NavigationMenuTrigger>
        <Link
          v-else
          class="font-sans-pt text-white text-[1.125rem] font-bold leading-6 hover:bg-indigo-dark group no-underline flex select-none items-center justify-between gap-2 p-3 outline-none rounded-md focus:ring-2 focus:ring-white"
          :class="{ 'rounded-b-none border-b-[3px] border-coral': item.is_current || item.is_parent }"
          :href="item.url"
        >
          <span
            class="text-left no-underline text-balance"
            aria-label="{`{{ item.title }} page`}"
          >
            {{ item.title }}
          </span>
        </Link>
        <NavigationMenuContent
          class="data-[motion=from-start]:animate-enterFromLeft data-[motion=from-end]:animate-enterFromRight data-[motion=to-start]:animate-exitToLeft data-[motion=to-end]:animate-exitToRight w-full"
        >
          <div class="container">
            <div class="flex flex-row py-4">
              <div class="grid grid-cols-[2fr_2fr_1fr] gap-8">
                <div
                  class="py-2"
                  v-for="childItem in item.children"
                  :key="childItem.title"
                >
                  <h3 class="text-opal font-sans-pt font-bold text-lg leading-8 mb-4 hidden">
                    {{ childItem.title }}
                  </h3>
                  <ul class="grid gap-y-[10px]">
                    <SiteNavigationListItem
                      v-for="grandChildItem in childItem.children"
                      :key="grandChildItem.title"
                      :href="grandChildItem.url"
                    >
                      {{ grandChildItem.title }}
                    </SiteNavigationListItem>
                  </ul>
                </div>
                <div class="border-l border-l-gray-100 py-2 px-4 min-h-[220px]">
                  <h3 class="text-white font-sans-pt font-bold text-lg leading-8 mb-4">Services available 24 hours</h3>
                  <ul class="grid gap-y-[10px]">
                    <SiteNavigationListItem href="tel:999">
                      <p>
                        <span class="font-bold">999</span>
                        (Emergency)
                      </p>
                    </SiteNavigationListItem>
                    <SiteNavigationListItem href="tel:116123">
                      <p><span class="font-bold">116 123</span></p>
                      <span>Samaritans</span>
                    </SiteNavigationListItem>
                    <SiteNavigationListItem href="sms:85258?body=SHOUT">
                      <p>
                        Text '
                        <span class="font-bold">SHOUT</span>
                        ' to
                        <span class="font-bold">85258</span>
                      </p>
                    </SiteNavigationListItem>
                    <SiteNavigationListItem href="tel:111">
                      <p><span class="font-bold">111</span></p>
                      <span>NHS24</span>
                    </SiteNavigationListItem>
                    <SiteNavigationListItem href="tel:0800 1111">
                      <p><span class="font-bold">0800 1111</span></p>
                      <span>Childline</span>
                    </SiteNavigationListItem>
                    <SiteNavigationListItem href="tel:0800 068 4141">
                      <p><span class="font-bold">0800 068 4141</span></p>
                      <span>HOPELINE247</span>
                    </SiteNavigationListItem>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </NavigationMenuContent>
      </NavigationMenuItem>
    </NavigationMenuList>
    <div
      class="absolute inset-x-0 top-full z-20 left-1/2 transform -translate-x-1/2 w-[100vw] min-h-[350px]"
      v-if="currentTrigger"
    >
      <NavigationMenuViewport
        class="bg-indigo-dark data-[state=open]:animate-scaleIn data-[state=closed]:animate-scaleOut h-[var(--reka-navigation-menu-viewport-height)] w-full origin-[top_center] overflow-hidden transition-[width,_height] duration-300 absolute left-0 right-0"
      />
    </div>
  </NavigationMenuRoot>
</template>
